import React from 'react';
import {
  Box, TextField, Slider, Typography, Button, IconButton, Stack,
  InputAdornment,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon    from '@mui/icons-material/Add';

const $adorn = <InputAdornment position="start">$</InputAdornment>;

export default function ForecastSliders({ params, onChange }) {
  const u = (key, val) => onChange({ ...params, [key]: val });

  const addChild = () =>
    u('children', [...(params.children || []),
      { name: 'Child', birth_year: new Date().getFullYear() + 1, monthly_cost: 1500 }]);

  const updChild = (i, f, v) => {
    const a = [...params.children];
    a[i] = { ...a[i], [f]: v };
    u('children', a);
  };

  const addGoal = () =>
    u('goals', [...(params.goals || []),
      { name: 'Goal', target_amount: 50000,
        target_year: new Date().getFullYear() + 5, target_month: 12 }]);

  const updGoal = (i, f, v) => {
    const a = [...params.goals];
    a[i] = { ...a[i], [f]: ['target_amount','target_year','target_month'].includes(f) ? Number(v) : v };
    u('goals', a);
  };

  const addPayment = () =>
    u('future_payments', [...(params.future_payments || []),
      { name: 'Payment', monthly_amount: 1000,
        start_year: new Date().getFullYear(), start_month: 1 }]);

  const updPayment = (i, f, v) => {
    const a = [...params.future_payments];
    const numFields = ['monthly_amount','start_year','start_month','end_year','end_month'];
    a[i] = { ...a[i], [f]: numFields.includes(f) ? Number(v) : v };
    u('future_payments', a);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom fontWeight={600}>
        Income &amp; Expenses
      </Typography>

      <Stack spacing={2} mb={3}>
        <TextField
          label="Primary Monthly Income" type="number" fullWidth
          value={params.primary_monthly_income || 0}
          onChange={(e) => u('primary_monthly_income', Number(e.target.value))}
          InputProps={{ startAdornment: $adorn }}
        />
        <TextField
          label="Base Monthly Expenses" type="number" fullWidth
          value={params.base_monthly_expenses || 0}
          onChange={(e) => u('base_monthly_expenses', Number(e.target.value))}
          InputProps={{ startAdornment: $adorn }}
        />
        <TextField
          label="Current Net Worth" type="number" fullWidth
          value={params.current_net_worth || 0}
          onChange={(e) => u('current_net_worth', Number(e.target.value))}
          InputProps={{ startAdornment: $adorn }}
        />
      </Stack>

      <Typography variant="subtitle2" gutterBottom>
        Inflation Rate: {((params.inflation_rate || 0.03) * 100).toFixed(1)}%
      </Typography>
      <Slider value={params.inflation_rate || 0.03} min={0} max={0.1} step={0.001}
        onChange={(_, v) => u('inflation_rate', v)} sx={{ mb: 2 }} />

      <Typography variant="subtitle2" gutterBottom>
        Investment Return: {((params.investment_return_rate || 0.07) * 100).toFixed(1)}%
      </Typography>
      <Slider value={params.investment_return_rate || 0.07} min={0} max={0.2} step={0.001}
        onChange={(_, v) => u('investment_return_rate', v)} sx={{ mb: 2 }} />

      <Typography variant="subtitle2" gutterBottom>
        Projection Years: {params.projection_years || 10}
      </Typography>
      <Slider value={params.projection_years || 10} min={1} max={50} step={1}
        onChange={(_, v) => u('projection_years', v)} sx={{ mb: 3 }} />

      {/* Children */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6" fontWeight={600}>Children</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addChild}>Add</Button>
      </Box>
      {(params.children || []).map((child, idx) => (
        <Stack key={idx} direction="row" spacing={1} mb={1} alignItems="center">
          <TextField size="small" label="Name" value={child.name}
            onChange={(e) => updChild(idx, 'name', e.target.value)} sx={{ width: 90 }} />
          <TextField size="small" label="Birth Yr" type="number" value={child.birth_year}
            onChange={(e) => updChild(idx, 'birth_year', Number(e.target.value))} sx={{ width: 90 }} />
          <TextField size="small" label="Cost/mo" type="number" value={child.monthly_cost}
            onChange={(e) => updChild(idx, 'monthly_cost', Number(e.target.value))}
            sx={{ width: 100 }} InputProps={{ startAdornment: $adorn }} />
          <IconButton size="small" color="error" onClick={() =>
            u('children', params.children.filter((_, i) => i !== idx))}>
            <DeleteIcon />
          </IconButton>
        </Stack>
      ))}

      {/* Goals */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} mt={3}>
        <Typography variant="h6" fontWeight={600}>Goals</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addGoal}>Add</Button>
      </Box>
      {(params.goals || []).map((goal, idx) => (
        <Stack key={idx} direction="row" spacing={1} mb={1} alignItems="center">
          <TextField size="small" label="Name" value={goal.name}
            onChange={(e) => updGoal(idx, 'name', e.target.value)} sx={{ width: 90 }} />
          <TextField size="small" label="Target" type="number" value={goal.target_amount}
            onChange={(e) => updGoal(idx, 'target_amount', e.target.value)}
            sx={{ width: 110 }} InputProps={{ startAdornment: $adorn }} />
          <TextField size="small" label="Year" type="number" value={goal.target_year}
            onChange={(e) => updGoal(idx, 'target_year', e.target.value)} sx={{ width: 80 }} />
          <IconButton size="small" color="error" onClick={() =>
            u('goals', params.goals.filter((_, i) => i !== idx))}>
            <DeleteIcon />
          </IconButton>
        </Stack>
      ))}

      {/* Future Payments */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} mt={3}>
        <Typography variant="h6" fontWeight={600}>Future Payments</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addPayment}>Add</Button>
      </Box>
      {(params.future_payments || []).map((pmt, idx) => (
        <Stack key={idx} direction="row" spacing={1} mb={1} alignItems="center" flexWrap="wrap">
          <TextField size="small" label="Name" value={pmt.name}
            onChange={(e) => updPayment(idx, 'name', e.target.value)} sx={{ width: 90 }} />
          <TextField size="small" label="Amt/mo" type="number" value={pmt.monthly_amount}
            onChange={(e) => updPayment(idx, 'monthly_amount', e.target.value)}
            sx={{ width: 110 }} InputProps={{ startAdornment: $adorn }} />
          <TextField size="small" label="Start Yr" type="number" value={pmt.start_year}
            onChange={(e) => updPayment(idx, 'start_year', e.target.value)} sx={{ width: 85 }} />
          <IconButton size="small" color="error" onClick={() =>
            u('future_payments', params.future_payments.filter((_, i) => i !== idx))}>
            <DeleteIcon />
          </IconButton>
        </Stack>
      ))}
    </Box>
  );
}
